import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ChevronDown, Send, Instagram, Facebook, Twitter } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6
  }
};
export function Contact() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const faqs = [{
    q: 'How can I apply for a program?',
    a: "You can apply for any of our programs by visiting the Programs page, selecting the initiative you're interested in, and clicking the 'Apply' button to fill out the registration form."
  }, {
    q: 'How do I make a donation?',
    a: 'Donations can be made securely through our Donate page via credit card, PayPal, or bank transfer. We accept both one-time and monthly recurring donations.'
  }, {
    q: 'Do you offer scholarships?',
    a: 'Yes, we offer various scholarships for our educational and tech programs. Eligibility requirements and application details are announced quarterly on our Blog and Newsletter.'
  }, {
    q: 'How can I volunteer?',
    a: 'Visit our Volunteer page to see open roles and submit an application. We have opportunities for both local (in-person) and remote volunteers.'
  }];
  return <div className="pt-0 bg-warmwhite" data-id="element-476" data-mp="minpe0hy8i8x">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-navy overflow-hidden" data-id="element-477" data-mp="qixs94ag2sjk">
        <div className="absolute inset-0 z-0" data-id="element-478" data-mp="vgpvuvkgc33l">
          <img src="https://images.unsplash.com/photo-1528301721190-186c3bd85418?q=80&w=2940&auto=format&fit=crop" alt="Contact Us" className="w-full h-full object-cover opacity-20" data-id="element-479" data-mp="vfy09kquu5db" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-transparent" data-id="element-480" data-mp="2bviqg94zersm" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-4xl" data-id="element-481" data-mp="1m6u52tagwdh6">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-482" data-mp="1h8iswbwp9qvs">
            Get in Touch
          </h1>
          <p className="text-xl text-white/80 leading-relaxed" data-id="element-483" data-mp="1w4hztj82pkzc">
            Have questions? Want to partner with us? Let's connect and make an
            impact together.
          </p>
        </div>
      </section>

      <section className="py-12 lg:py-16" data-id="element-484" data-mp="13hng4oj2ypmx">
        <div className="container mx-auto px-4 sm:px-6 md:px-12" data-id="element-485" data-mp="yhstix6c8m7o">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16" data-id="element-486" data-mp="yjm1cfsotch9">
            {/* Contact Info & Form */}
            <div className="w-full lg:w-2/3" data-id="element-487" data-mp="1d8ryly7yssmi">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6 mb-8 lg:mb-12" data-id="element-488" data-mp="18fozzbobyszg">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-softgray text-center" data-id="element-489" data-mp="kpcknfqr9tyu">
                  <div className="w-12 h-12 bg-gold/10 text-gold rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-490" data-mp="fq89ijk9tm5v">
                    <MapPin className="w-6 h-6" data-id="element-491" data-mp="2rdbva4814x1w" />
                  </div>
                  <h3 className="font-bold text-navy mb-2" data-id="element-492" data-mp="2w6500s3i05dz">Address</h3>
                  <p className="text-sm text-mediumgray" data-id="element-493" data-mp="2hgz2d8kb6n87">
                    123 Impact Way, Suite 100
                    <br data-id="element-494" data-mp="2mf0hjiem62ee" />
                    New York, NY 10001
                  </p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-softgray text-center" data-id="element-495" data-mp="3a4ux47qbi52c">
                  <div className="w-12 h-12 bg-gold/10 text-gold rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-496" data-mp="3f365ee0ys7ig">
                    <Phone className="w-6 h-6" data-id="element-497" data-mp="3146hj8l9zwnc" />
                  </div>
                  <h3 className="font-bold text-navy mb-2" data-id="element-498" data-mp="35507e7ia3liv">Phone</h3>
                  <p className="text-sm text-mediumgray" data-id="element-499" data-mp="1ppj4ujh7rg37">
                    +1 (555) 123-4567
                    <br data-id="element-500" data-mp="1tqmmnbitnm72" />
                    Mon-Fri, 9am-5pm EST
                  </p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-softgray text-center" data-id="element-501" data-mp="29yj1ltr1d4a9">
                  <div className="w-12 h-12 bg-gold/10 text-gold rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-502" data-mp="256soxh4mm4fo">
                    <Mail className="w-6 h-6" data-id="element-503" data-mp="208zhylvi16ec" />
                  </div>
                  <h3 className="font-bold text-navy mb-2" data-id="element-504" data-mp="1v9msdtd8tl82">Email</h3>
                  <p className="text-sm text-mediumgray" data-id="element-505" data-mp="1v7a6x68a0sq9">
                    hello@pochglobal.org
                    <br data-id="element-506" data-mp="2suit73g3q5tb" />
                    Support: help@pochglobal.org
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 sm:p-8 md:p-12 rounded-3xl shadow-premium border border-softgray" data-id="element-507" data-mp="2ixxpqmjqd2rl">
                <h2 className="text-2xl lg:text-3xl font-serif font-bold text-navy mb-6 lg:mb-8" data-id="element-508" data-mp="2ew2hey3dtp4b">
                  Send us a Message
                </h2>
                <form className="space-y-4 lg:space-y-6" data-id="element-509" data-mp="13hynfrwso0z9">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6" data-id="element-510" data-mp="13gdsi16kig0v">
                    <div data-id="element-511" data-mp="3f6ll6qz6f0r9">
                      <label className="block text-sm font-medium text-mediumgray mb-2" data-id="element-512" data-mp="3f7516259nf2n">
                        Name *
                      </label>
                      <input type="text" className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy bg-warmwhite" placeholder="John Doe" data-id="element-513" data-mp="3o5guwd1fiszb" />
                    </div>
                    <div data-id="element-514" data-mp="6o5vk82b31pv">
                      <label className="block text-sm font-medium text-mediumgray mb-2" data-id="element-515" data-mp="6rlczhjgcmug">
                        Email *
                      </label>
                      <input type="email" className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy bg-warmwhite" placeholder="john@example.com" data-id="element-516" data-mp="fqf8f4l1r76f" />
                    </div>
                  </div>
                  <div data-id="element-517" data-mp="3kuag19juijrv">
                    <label className="block text-sm font-medium text-mediumgray mb-2" data-id="element-518" data-mp="3ftw7lqec17tb">
                      Reason for Inquiry *
                    </label>
                    <select className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy bg-warmwhite text-mediumgray" data-id="element-519" data-mp="3tr19de70jkir">
                      <option data-id="element-520" data-mp="7k31slndqp2z">General Inquiry</option>
                      <option data-id="element-521" data-mp="2ncna2r841jo">Program Application</option>
                      <option data-id="element-522" data-mp="2kqejzsljfzv">Partnership Opportunity</option>
                      <option data-id="element-523" data-mp="gd6e2qmagvw1">Donation Question</option>
                    </select>
                  </div>
                  <div data-id="element-524" data-mp="cdxh7cvychw9">
                    <label className="block text-sm font-medium text-mediumgray mb-2" data-id="element-525" data-mp="ceqruoy17rjo">
                      Message *
                    </label>
                    <textarea rows={5} className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy bg-warmwhite" placeholder="How can we help you?" data-id="element-526" data-mp="le3rkpwct1r7"></textarea>
                  </div>
                  <button type="submit" className="w-full py-4 bg-navy hover:bg-navy-light text-white rounded-xl font-bold transition-all shadow-md flex items-center justify-center gap-2" data-id="element-527" data-mp="v0euq6yd9tfe">
                    <Send className="w-5 h-5" data-id="element-528" data-mp="qz31lrgaaq2g" /> Send Message
                  </button>
                  <p className="text-center text-sm text-mediumgray mt-4" data-id="element-529" data-mp="cagn1ryhe2ca">
                    We aim to respond to all inquiries within 24-48 hours.
                  </p>
                </form>
              </div>
            </div>

            {/* FAQ Sidebar */}
            <div className="lg:w-1/3 w-full" data-id="element-530" data-mp="3ak2o2j4jpn1">
              <div data-id="element-531" data-mp="7b3vwv0ku93w">
                <div className="flex items-center justify-between mb-6" data-id="element-532" data-mp="7aaodrtsnfkp">
                  <h3 className="text-xl lg:text-2xl font-serif font-bold text-navy" data-id="element-533" data-mp="nql5lj0uykhf">
                    Frequently Asked Questions
                  </h3>
                  <Link to="/faq" className="text-xs lg:text-sm font-bold text-gold hover:underline" data-id="element-534" data-mp="isjnp1f64r7j">
                    View All FAQs
                  </Link>
                </div>
                <div className="space-y-4" data-id="element-535" data-mp="8sstz1yfmg8i">
                {faqs.map((faq, i) => <div key={i} className="bg-white border border-softgray rounded-2xl overflow-hidden" data-id="element-536" data-mp="4t26p8846i8q" data-mpi={i}>
                    <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-warmwhite transition-colors text-left" data-id="element-537" data-mp="3r7layc3dkh1h">
                      <span className="font-bold text-navy text-sm" data-id="element-538" data-mp="3h8vxqwmfbkrk">
                        {faq.q}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-mediumgray transition-transform duration-300 shrink-0 ${activeFaq === i ? 'rotate-180' : ''}`} data-id="element-539" data-mp="1spruymuir22s" />
                    </button>
                    <AnimatePresence data-id="element-540" data-mp="21q6hfpj7yn7v">
                      {activeFaq === i && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} className="overflow-hidden" data-id="element-541" data-mp="26patcnxj2z1w">
                          <div className="px-6 pb-4 text-mediumgray text-sm border-t border-softgray pt-3" data-id="element-542" data-mp="qb11vkxzou6u">
                            {faq.a}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>)}
              </div>

              <div className="mt-12" data-id="element-543" data-mp="yvs17ot7wwvf">
                <h3 className="text-2xl font-serif font-bold text-navy mb-4" data-id="element-544" data-mp="ywtg64dcu3if">
                  Follow Us
                </h3>
                <p className="text-mediumgray mb-6" data-id="element-545" data-mp="13u3bu1rc7bep">
                  Connect with us on social media for daily updates and inspiration.
                </p>
                <div className="flex gap-4" data-id="element-546" data-mp="b5e9y0igj21w">
                  <a href="#" className="w-12 h-12 rounded-full bg-white border border-softgray flex items-center justify-center text-navy hover:bg-gold hover:text-white hover:border-gold transition-all shadow-sm" data-id="element-547" data-mp="b434lw9d5m8o">
                    <Instagram className="w-5 h-5" data-id="element-548" data-mp="kxtn8kit72gg" />
                  </a>
                  <a href="#" className="w-12 h-12 rounded-full bg-white border border-softgray flex items-center justify-center text-navy hover:bg-gold hover:text-white hover:border-gold transition-all shadow-sm" data-id="element-549" data-mp="3jlqwaxp5zbgm">
                    <Facebook className="w-5 h-5" data-id="element-550" data-mp="25nmwcc0m84vo" />
                  </a>
                  <a href="#" className="w-12 h-12 rounded-full bg-white border border-softgray flex items-center justify-center text-navy hover:bg-gold hover:text-white hover:border-gold transition-all shadow-sm" data-id="element-551" data-mp="21ovujrl7fz28">
                    <Twitter className="w-5 h-5" data-id="element-552" data-mp="1rsaqp9kx8tzz" />
                  </a>
                </div>
              </div>

              <div className="mt-12 rounded-3xl overflow-hidden h-64 bg-lightgray relative shadow-sm border border-softgray" data-id="element-553" data-mp="1msy6kwlfq113">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.617548123844!2d-73.9878531!3d40.7484405!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259a9b3117469%3A0xd134e199a405a163!2sEmpire%20State%20Building!5e0!3m2!1sen!2sus!4v1680000000000!5m2!1sen!2sus" width="100%" height="100%" style={{
                  border: 0
                }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Office Location Map" data-id="element-554" data-mp="1dy9yyzse0794"></iframe>
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}